<template>
  <div>
    <div class="mb-2 sm:grid sm:grid-cols-2 sm:justify-items-stretch">
      <div class="mr-2 flex gap-2 justify-self-start">
        <Button variant="outline" size="sm" @click="selectAllRecords">
          <vue-feather type="check-square" class="h-4 w-4 mr-1" />
          Select All
        </Button>
        <template v-if="selectedContacts.length > 0">
          <Button variant="outline" size="sm" @click="deselectAllRecords">
            <vue-feather type="x-square" class="h-4 w-4 mr-1" />
            Deselect All
          </Button>
          <Button variant="outline" size="sm" @click="markSelectedAsRead">
            <vue-feather type="file-text" class="h-4 w-4 mr-1" />
            Mark as Read
          </Button>
        </template>
      </div>
      <div class="justify-self-end flex">
        <Button variant="outline" size="sm" @click="exportMessages">
          <vue-feather type="download" class="h-4 w-4 mr-1" />
          Export
        </Button>
      </div>
    </div>
    <div v-if="!loadingSpinnerStore.loadingSpinner" class="flex flex-col">
      <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
        <div class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200">
          <div
            v-if="inboxContacts.length == 0"
            class="px-6 py-3 border-b border-gray-200 bg-gray-50 text-center text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
          >
            Send your first message by creating a contact and using the chat feature!
          </div>
          <table class="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th
                  class="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                >
                  Name
                </th>
                <th class="px-6 py-3 border-b border-gray-200 bg-gray-50"></th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr
                v-for="inboxContact of inboxContacts"
                :key="inboxContact.id"
                class="cursor-pointer hover:bg-gray-50"
                @click="handleInboxChat(inboxContact.id, inboxContact.name)"
              >
                <td class="px-6 py-4 border-b border-gray-200">
                  <div class="flex items-center">
                    <input
                      type="checkbox"
                      class="h-4 w-4 shrink-0 cursor-pointer text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      :checked="selectedContacts.includes(inboxContact.id)"
                      @click.stop
                      @click="recordChecked($event, inboxContact.id)"
                    />
                    <div class="ml-4">
                      <div class="text-sm leading-5 font-medium text-gray-900 flex gap-2">
                        <router-link
                          :to="`/contacts/${inboxContact.id}`"
                          class="text-indigo-600 hover:text-indigo-900"
                          >{{ inboxContact.name }}</router-link
                        >
                        <Badge v-if="inboxContact.status == 'Unread'" variant="green">
                          {{ inboxContact.status }}
                        </Badge>
                      </div>
                      <div class="text-sm leading-5 text-gray-500">
                        {{ inboxContact.body }}
                      </div>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4 border-b border-gray-200 text-sm leading-5 text-gray-900 truncate">
                  {{ fromNow(inboxContact.created_at) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <LoadingList />

    <Pagination :start-entry="startEntry" :end-entry="endEntry" :total="total" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { toast } from 'vue-sonner';
import { useMultiChatStore } from '@/components/shared/MultiChat';
import LoadingList from '../shared/LoadingList.vue';
import Pagination from '../shared/Pagination.vue';
import DateUtility from '../../utilities/dates';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner.ts';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Api } from '@/api';
import { useCableChannel } from '@/composables/useCableChannel';
import type { ContactMessageStatus } from '@/api/entity_types/ContactMessage';

type InboxContact = {
  id: number;
  name: string;
  body: string;
  status: ContactMessageStatus;
  created_at: string;
};

type InboxChannelData = {
  records: InboxContact[];
  total: number;
  start_entry: number;
  end_entry: number;
};

const route = useRoute();
const loadingSpinnerStore = useLoadingSpinnerStore();
const chatStore = useMultiChatStore();

const inboxContacts = ref<InboxContact[]>([]);
const startEntry = ref(0);
const endEntry = ref(0);
const total = ref(0);
const selectedContacts = ref<number[]>([]);

const fromNow = (date: string) => DateUtility.fromNow(date);

const selectAllRecords = () => {
  for (const contact of inboxContacts.value) {
    selectedContacts.value.push(contact.id);
  }
  selectedContacts.value = [...new Set(selectedContacts.value)];
};

const deselectAllRecords = () => {
  selectedContacts.value = [];
};

const markSelectedAsRead = async () => {
  loadingSpinnerStore.showLoadingSpinner();
  try {
    const response = await Api.ContactMessages.markBulkContactMessagesAsRead(selectedContacts.value);
    if (response) {
      selectedContacts.value = [];
      await getContactMessages();
      toast.success('Messages marked as read', {
        description: 'Your selected messages were marked as read.',
      });
    } else {
      toast.error('Messages marked as read error');
    }
  } finally {
    loadingSpinnerStore.hideLoadingSpinner();
  }
};

const recordChecked = (e: Event, recordId: number) => {
  const target = e.currentTarget as HTMLInputElement;
  if (target.checked) {
    selectedContacts.value.push(recordId);
  } else {
    selectedContacts.value = selectedContacts.value.filter(item => item != recordId);
  }
};

const handleInboxChat = (id: number, name: string) => {
  chatStore.openChat({ id, name, canReceiveSms: true });
};

const getContactMessages = async () => {
  const pageQuery = route.query.page ? `page=${route.query.page}` : '';
  const keywordQuery = route.query.keyword_id ? `keyword_id=${route.query.keyword_id}` : '';

  loadingSpinnerStore.showLoadingSpinner();
  const response = await Api.ContactMessages.listContactMessages({ pageQuery, keywordQuery });
  loadingSpinnerStore.hideLoadingSpinner();

  if (response?.records) {
    inboxContacts.value = response.records.map(record => ({
      id: record.id,
      name: record.name || `${record.id}`,
      body: record.body,
      status: record.status,
      created_at: record.created_at,
    }));

    total.value = response.total;
    startEntry.value = response.start_entry;
    endEntry.value = response.end_entry;
  } else {
    toast.error('Failed To Load Inbox', { description: 'Messages failed to load. Please try again.' });
  }
};

const exportMessages = () => {
  loadingSpinnerStore.showLoadingSpinner();
  fetch('/api/contact_messages/download_csv', {
    headers: { 'Content-Type': 'text/csv' },
  })
    .then(response => {
      loadingSpinnerStore.hideLoadingSpinner();
      return response.blob();
    })
    .then(data => {
      const file = window.URL.createObjectURL(data);
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.style.display = 'none';
      a.href = file;
      a.download = 'message_export.csv';
      a.click();
      window.URL.revokeObjectURL(file);
    });
};

useCableChannel<InboxChannelData>({
  channel: 'ContactMessagesInboxChannel',
  onMessage: data => {
    inboxContacts.value = data.records.map(rec => ({
      id: rec.id,
      name: rec.name || `${rec.id}`,
      body: rec.body,
      status: rec.status,
      created_at: rec.created_at,
    }));
    total.value = data.total;
    startEntry.value = data.start_entry;
    endEntry.value = data.end_entry;
  },
});

onMounted(() => {
  getContactMessages();
});
</script>
